import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { Button } from '@ctw/shared/components/button';
import { DropdownMenuSimple } from '@ctw/shared/components/dropdown/simple';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type {
  LegacyMinRecordItem,
  LegacyRowActionsConfigProp,
  LegacyRowActionsProps,
} from '@ctw/shared/components/tables/legacy-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { TelemetryBoundary } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronDown, faChevronUp, faFire } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Resource } from 'fhir/r4';
import { useCallback, useState } from 'react';
import { useToggleRead } from '../hooks/use-toggle-read';
import { useViewFhirModal } from './view-fhir-modal';

export type AdditionalResourceActionsProps<T extends LegacyMinRecordItem> = {
  onToggleRead?: () => void;
  rowActions?: (r: T) => LegacyRowActionsConfigProp<T>;
  enableReadActions?: boolean;
  enableViewFhirButton?: boolean;
  // Will adjust the way the dropdown menu appears if the content will be rendered in a footer
  isInFooter?: boolean;
};

export const useAdditionalResourceActions = <T extends Resource, M extends FHIRModel<T>>({
  onToggleRead,
  rowActions,
  enableViewFhirButton = true,
  enableReadActions,
  isInFooter = false,
}: AdditionalResourceActionsProps<M>) => {
  const { ctwBehavioralFlags } = useCTW();
  const [selectedAction, setSelectedAction] = useState('card');
  const readActions = useReadActions(onToggleRead, enableReadActions);

  return ({ record, onSuccess, stacked = false }: LegacyRowActionsProps<M>) => {
    const extraActions = readActions(record) ?? [];
    const actions = rowActions?.(record) ?? [];
    const combinedActions = [...extraActions, ...actions];
    if (combinedActions.length === 0 && !ctwBehavioralFlags.enableViewFhirButton) {
      return null;
    }

    if (stacked) {
      return (
        <TelemetryBoundary
          boundaryName="ResourceActionsDropdown"
          initialContext={{ resourceType: record.resourceType, resourceId: record.id }}
        >
          {({ telemetry }) => (
            <div className={tw`flex h-[36px] items-stretch space-x-2`}>
              {ctwBehavioralFlags.enableViewFhirButton && <ViewFHIR resource={record.resource} />}
              {combinedActions.length > 0 && (
                <DropdownMenuSimple
                  align="end"
                  buttonClassName={twx(
                    'flex h-[36px] items-center items-stretch justify-center border-none bg-transparent',
                  )}
                  onItemSelect={(item) => {
                    const selectedOption = combinedActions.filter(
                      (action) => action.text === item.key,
                    )[0];

                    telemetry.trackInteraction('resource_action_click', {
                      resourceActionName: item.name,
                    });

                    setSelectedAction(selectedOption.text);
                    setTimeout(() => {
                      // This is a hack to make sure the dropdown menu closes before the action is executed to avoid
                      // react state updates on unmounted components
                      void selectedOption.onClick(record, onSuccess);
                    }, 1);
                  }}
                  items={combinedActions.map((item) => ({
                    key: item.text,
                    name: item.text,
                    display: item.render?.({ record, onSuccess, stacked }) ?? item.text,
                    isSelected: selectedAction === item.text,
                    disabled: item.disabled,
                  }))}
                >
                  <div
                    className={tw`flex h-full items-center space-x-2 rounded-md border border-border-main border-transparent border-solid bg-white px-4 py-2 font-medium text-sm capitalize shadow hover:bg-background-hover`}
                  >
                    <span
                      className={tw`!border-r-1 divide-background-disabled border-0 border-solid py-1 pr-2`}
                    >
                      select action
                    </span>
                    <FontAwesomeIcon
                      icon={isInFooter ? faChevronUp : faChevronDown}
                      className={tw`w-3`}
                    />
                  </div>
                </DropdownMenuSimple>
              )}
            </div>
          )}
        </TelemetryBoundary>
      );
    }

    return (
      <TelemetryBoundary boundaryName="ResourceActionButtons">
        {() => (
          <div className={tw`flex h-[36px] items-stretch space-x-2`}>
            {ctwBehavioralFlags.enableViewFhirButton && enableViewFhirButton && (
              <ViewFHIR resource={record.resource} />
            )}
            {combinedActions.map(
              ({ text, testId, onClick, disabled = false, className, render }) => (
                <Button
                  key={text}
                  type="button"
                  disabled={disabled}
                  variant="unstyled"
                  telemetryInteractionName="resource_action_click"
                  telemetryContext={{
                    resourceActionName: text,
                  }}
                  className={twx(className, 'row-action h-[36px] min-h-[36px]')}
                  testId={testId}
                  onClick={() => {
                    void onClick(record, onSuccess);
                  }}
                >
                  {render?.({ record, onSuccess, stacked }) ?? text}
                </Button>
              ),
            )}
          </div>
        )}
      </TelemetryBoundary>
    );
  };
};

function useReadActions(onToggleRead?: () => void, enableReadActions = false) {
  const { requestContext } = useCTW();

  const { isLoading: isToggleReadLoading, toggleRead } = useToggleRead();

  return useCallback(
    (record: FHIRModel<Resource>): LegacyRowActionsConfigProp<FHIRModel<Resource>> => {
      const readLabel = record.isRead ? 'Unread' : 'Read';

      // In production, we want to avoid non-builder users from inadvertently marking records as read
      // In lower environments this is allowed for demos/testing
      const disableReadButton =
        requestContext.env === 'prod' && requestContext.authTokenState.userType !== 'builder';
      if (!enableReadActions || record.ownedByBuilder(requestContext.builderId)) {
        return [];
      }
      return [
        {
          className:
            'rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm border border-solid border-border-main bg-white capitalize text-content-subtle hover:bg-background-hover',
          disabled: disableReadButton || isToggleReadLoading,
          text: readLabel,
          onClick: async () => {
            await toggleRead(record);
            onToggleRead?.();
          },
          render() {
            return (
              <div className={tw`flex`}>
                {isToggleReadLoading && (
                  <LoadingSpinner message="Loading..." className={tw`mx-4 align-middle`} />
                )}
                {!isToggleReadLoading && readLabel}
              </div>
            );
          },
        },
      ];
    },
    [
      enableReadActions,
      isToggleReadLoading,
      onToggleRead,
      requestContext.authTokenState.userType,
      requestContext.builderId,
      requestContext.env,
      toggleRead,
    ],
  );
}

interface ViewFHIRProps {
  name?: string;
  resource: Resource;
}

function ViewFHIR({ name = 'View FHIR', resource }: ViewFHIRProps) {
  const openModal = useViewFhirModal();

  return (
    <Button
      className={tw`h-full min-h-[36px]`}
      type="button"
      ariaLabel={name}
      variant="secondary"
      telemetryInteractionName="resource_action_click"
      telemetryContext={{
        resourceActionName: name,
      }}
      onClick={() => {
        openModal(resource, name);
      }}
    >
      <span className={tw`flex h-4 w-4 items-center justify-center`}>
        <FontAwesomeIcon icon={faFire} className={tw`h-4 w-4`} />
      </span>
    </Button>
  );
}
